/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-10 21:10:15
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-09-03 11:50:50
 */
import { useCallback, useState, useEffect } from 'react'
import sensors from '../../../utils/sensors'
import parseQueryString from '../../../utils/parseQueryString'

// 整改数据类型
interface RectifyData {
  totalCount?: number
  rectifiedCount?: number
  unRectifiedCount?: number
  [key: string]: any
}

// 埋点选项类型
interface TrackOptions {
  startDate?: string
  endDate?: string
  sourceType?: string
  [key: string]: any
}

// 状态映射类型
interface StatusMap {
  [key: number]: string
}

/**
 * 整改记录页面埋点Hook
 * @returns {Object} 埋点相关的方法
 */
const useRectifyAnalytics = () => {
  // 保存来源地点状态
  const [fromPlace, setFromPlace] = useState('侧边栏点击')

  // 页面初始化时判断来源
  useEffect(() => {
    const { recordID } = parseQueryString()
    if (recordID) {
      // 如果URL中有recordID，说明是从照片预览页来的
      setFromPlace('照片预览页')
    } else {
      // 否则都归类为侧边栏点击（包括页面刷新的情况）
      setFromPlace('侧边栏点击')
    }
  }, [])

  /**
   * 上报整改记录页面埋点
   * @param {String} clickItem - 点击的元素
   * @param {Object} data - 整改记录数据
   * @param {Object} options - 额外的埋点参数
   */
  const trackRectifyEvent = useCallback(
    (clickItem: string, data: RectifyData = {}, options: TrackOptions = {}) => {
      // 确保数据对象存在且属性有效
      const safeData = data || {}
      const totalCount =
        safeData.totalCount !== undefined ? safeData.totalCount : 0
      const rectifiedCount =
        safeData.rectifiedCount !== undefined ? safeData.rectifiedCount : 0
      const unRectifiedCount =
        safeData.unRectifiedCount !== undefined ? safeData.unRectifiedCount : 0

      // 构建基础埋点数据
      const trackData: Record<string, any> = {
        clickItem,
        totalNum: totalCount,
        number1: rectifiedCount,
        number2: unRectifiedCount,
        fromPlace, // 添加来源字段
        track_sign: 'aworkgroup.b7316.action.web_click_page_rectify_records',
        requirement_id: 156,
        ...options
      }

      // 根据不同的点击元素添加额外属性
      if (
        [
          'clickFilterDate',
          'clickFilter',
          'clickDownloadRecord',
          'clickSaveExcel'
        ].includes(clickItem) &&
        options.startDate &&
        options.endDate
      ) {
        trackData.startDate = options.startDate
        trackData.endDate = options.endDate
      }

      if (
        [
          'clickFilterType',
          'clickFilter',
          'clickDownloadRecord',
          'clickSaveExcel'
        ].includes(clickItem) &&
        options.sourceType
      ) {
        trackData.sourceType = options.sourceType
      }

      // 上报埋点
      sensors.track('web_click_page_rectify_records', trackData)
    },
    [fromPlace]
  )

  /**
   * 获取任务状态对应的sourceType
   * @param {Number} status - 任务状态 0:全部, 1:未完成, 2:已完成
   * @returns {String} sourceType值
   */
  const getSourceTypeByStatus = useCallback((status: number) => {
    const sourceTypeMap: StatusMap = {
      0: 'all',
      1: 'unverify',
      2: 'verified'
    }
    return sourceTypeMap[status] || 'all'
  }, [])

  return {
    trackRectifyEvent,
    getSourceTypeByStatus
  }
}

export default useRectifyAnalytics
